import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChatMessageDto } from '../types/community'
import { getRoomMessages, postMessage } from '../lib/api/rooms'
import { apiError } from '../lib/api/client'
import { useToast } from '../contexts/ToastContext'
import { usePoll } from './usePoll'

const HOLD_TOAST = 'Held for review — that looked like a phone number, school or off-site handle.'
const UNAVAILABLE = 'This room is closed or no longer available.'

/** Server snapshots are authoritative, including removals after a safety block. */
export function useRoomChat(roomId: string | undefined, initial: ChatMessageDto[] | undefined, enabled: boolean, actingPersonId?: string) {
  const { flash } = useToast()
  const [messages, setMessages] = useState<ChatMessageDto[]>(initial ?? [])
  const [sending, setSending] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const generation = useRef(0)
  const snapshotVersion = useRef(0)
  const inFlight = useRef(false)
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  useEffect(() => {
    generation.current += 1
    inFlight.current = false
    setSending(false)
    setMessages([])
    setConnectionError(null)
    setUnavailable(false)
    return () => { generation.current += 1 }
  }, [roomId, actingPersonId])

  useEffect(() => {
    if (initial) {
      snapshotVersion.current += 1
      setMessages(initial)
    }
  }, [initial, roomId, actingPersonId])

  const appendNew = useCallback((incoming: ChatMessageDto[]) => {
    setMessages(previous => {
      const seen = new Set(previous.map(message => message.id))
      return [...previous, ...incoming.filter(message => !seen.has(message.id))]
    })
  }, [])

  const fail = useCallback((error: unknown) => {
    const status = apiError(error).status
    if (status && [403, 404, 410].includes(status)) {
      setUnavailable(true)
      setMessages([])
      setConnectionError(UNAVAILABLE)
    } else {
      setConnectionError('Chat could not connect. Your message is kept; retry sending or wait for reconnection.')
    }
  }, [])

  const poll = useCallback(() => {
    if (!roomId) return
    const requestGeneration = generation.current
    const requestSnapshot = snapshotVersion.current
    const current = messagesRef.current
    const after = current.length ? current[current.length - 1].id : undefined
    void getRoomMessages(roomId, after).then(newer => {
      if (requestGeneration !== generation.current || requestSnapshot !== snapshotVersion.current) return
      appendNew(newer)
      setConnectionError(null)
    }).catch(error => {
      if (requestGeneration === generation.current && requestSnapshot === snapshotVersion.current) fail(error)
    })
  }, [roomId, appendNew, fail])
  usePoll(poll, 3_000, enabled && !!roomId && !unavailable)

  const send = useCallback(async (body: string): Promise<boolean> => {
    const trimmed = body.trim()
    if (!trimmed || !roomId || !enabled || unavailable || inFlight.current) return false
    const requestGeneration = generation.current
    inFlight.current = true
    setSending(true)
    try {
      const result = await postMessage(roomId, { body: trimmed })
      if (requestGeneration !== generation.current) return false
      setConnectionError(null)
      if (result.held) flash(HOLD_TOAST)
      else if (result.message) appendNew([result.message])
      return true
    } catch (error) {
      if (requestGeneration === generation.current) fail(error)
      return false
    } finally {
      if (requestGeneration === generation.current) {
        inFlight.current = false
        setSending(false)
      }
    }
  }, [roomId, enabled, unavailable, flash, appendNew, fail])

  return { messages, send, sending, connectionError, unavailable }
}
